"use client";

import Link from "next/link";
import { useState, type FormEvent } from "react";
import { track } from "@/lib/analytics";

const EMAIL = "ask@completehealthclinic.co.uk";

const inputClass =
  "w-full rounded-xl border border-plum-100 bg-white px-4 py-3 text-ink placeholder:text-ink/40 transition focus:border-plum-500 focus:outline-none focus:ring-2 focus:ring-plum-200";
const labelClass = "mb-1.5 block text-sm font-semibold text-plum-900";

/**
 * There is no form backend, so this composes a pre-filled email in the visitor's own
 * mail app. We never claim the message has been sent.
 */
export function EnquiryForm({
  subject = "Website enquiry",
  card = false,
  messagePlaceholder = "Message",
}: {
  subject?: string;
  card?: boolean;
  messagePlaceholder?: string;
}) {
  const [opened, setOpened] = useState(false);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const get = (key: string) => String(data.get(key) ?? "").trim();
    const name = `${get("firstName")} ${get("lastName")}`.trim();
    const body = [
      `Name: ${name}`,
      `Email: ${get("email")}`,
      `Phone: ${get("phone") || "Not provided"}`,
      "",
      get("message"),
    ].join("\n");
    const href = `mailto:${EMAIL}?subject=${encodeURIComponent(`${subject} from ${name}`)}&body=${encodeURIComponent(body)}`;
    // We can only know the mail app was opened, not that the email was sent.
    track({ type: "form", form: "enquiry", action: "email-opened", detail: subject });
    window.location.href = href;
    setOpened(true);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={card ? "space-y-5 rounded-3xl bg-white p-6 text-ink shadow-xl shadow-plum-900/10 ring-1 ring-plum-100 sm:p-8" : "space-y-5"}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="enq-first" className={labelClass}>
            First Name <span className="text-plum-500">*</span>
          </label>
          <input id="enq-first" name="firstName" required autoComplete="given-name" placeholder="First Name" className={inputClass} />
        </div>
        <div>
          <label htmlFor="enq-last" className={labelClass}>
            Last Name <span className="text-plum-500">*</span>
          </label>
          <input id="enq-last" name="lastName" required autoComplete="family-name" placeholder="Last Name" className={inputClass} />
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="enq-email" className={labelClass}>
            Email <span className="text-plum-500">*</span>
          </label>
          <input id="enq-email" name="email" type="email" required autoComplete="email" placeholder="Email" className={inputClass} />
        </div>
        <div>
          <label htmlFor="enq-phone" className={labelClass}>
            Phone
          </label>
          <input id="enq-phone" name="phone" type="tel" autoComplete="tel" placeholder="Phone" className={inputClass} />
        </div>
      </div>
      <div>
        <label htmlFor="enq-message" className={labelClass}>
          Message
        </label>
        <textarea id="enq-message" name="message" rows={5} placeholder={messagePlaceholder} className={inputClass} />
      </div>
      <label className="flex items-start gap-3 text-sm text-ink/80">
        <input type="checkbox" name="consent" required className="mt-0.5 size-4 shrink-0 accent-plum-700" />
        <span>
          By clicking this I agree to the terms of the{" "}
          <Link href="/privacy-policy/" className="font-semibold text-plum-700 underline underline-offset-2">
            Privacy Policy
          </Link>
          .
        </span>
      </label>
      <div className="flex flex-wrap items-center gap-4">
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-plum-700 px-8 py-3.5 font-semibold text-white transition hover:bg-plum-900"
        >
          Send
        </button>
        <p className="text-sm text-ink/60">Opens a ready-to-send email in your mail app.</p>
      </div>
      {opened && (
        <p role="status" className="rounded-2xl bg-plum-50 p-4 text-sm leading-relaxed text-plum-900">
          Your email app should now open with your message ready to send. If nothing happened, please email us directly at{" "}
          <a href={`mailto:${EMAIL}`} className="font-semibold underline underline-offset-2">
            {EMAIL}
          </a>
          .
        </p>
      )}
    </form>
  );
}
